/**
 * "Use in…" - hand files from Files to the rest of the platform (F-DMS).
 *
 * Files is the firm's document store; these actions start work FROM it
 * instead of making the user download a document and upload it somewhere
 * else. The backend reads the files server-side (`/…/from-files`), so nothing
 * travels through the browser:
 *
 *   Start a review       → pick a playbook   → POST /reviews/from-files
 *   Add to a table       → pick / new table  → POST /tables/:uuid/documents/from-files
 *   Ask in Research      → new research chat → POST /research/chat-groups/:uuid/attachments/from-files
 *   Add to a matter      → pick a matter     → POST /matters/:uuid/documents/from-files
 *   Link to a project    → pick a project    → POST /projects/:uuid/resources (per file)
 *   Run legislation scan → one file, the scan side panel
 *
 * Each target opens where the files went, except the links (matter, project),
 * which confirm with a toast.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import {
  PiBriefcaseLight, PiChatCircleDotsLight, PiFileMagnifyingGlassLight, PiKanbanLight,
  PiMagnifyingGlassLight, PiPlusLight, PiScalesLight, PiTableLight,
} from 'react-icons/pi';
import apis from '../../utils/apis';
import { AppDispatch } from '../../store';
import { showModal } from '../../store/actions/modalActions';
import { showSidePanel } from '../../store/actions/sidePanelActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import ModalShell from '../shared/ModalShell';
import Button from '../shared/TailwindComponents/Button';
import Skeleton from '../shared/TailwindComponents/Skeleton';

export interface FilesSelection {
  uuids: string[];
  /** For titles and the single-file scan. */
  names: string[];
}

interface Target { value: string; label: string; hint?: string }

interface PickerState {
  title: string;
  icon: React.ReactNode;
  confirmLabel: string;
  load: (search: string) => Promise<Target[]>;
  /** "New table" style creation from a typed name. */
  createLabel?: string;
  onPick: (target: Target | { create: string }) => Promise<void>;
}

const errorText = (e: any, fallback: string) =>
  e?.response?.data?.error || e?.response?.data?.message || fallback;

/** One list-and-confirm modal for every "pick where these go" step. */
const TargetPicker: React.FC<{ state: PickerState; t: TFunction; onClose: () => void }> = ({ state, t, onClose }) => {
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<Target[] | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    const handle = setTimeout(() => {
      setItems(null);
      state.load(search).then((r) => { if (alive) setItems(r); }).catch(() => { if (alive) setItems([]); });
    }, 250);
    return () => { alive = false; clearTimeout(handle); };
  }, [search, state]);

  const confirm = async () => {
    setBusy(true);
    try {
      const target = chosen === '__new__'
        ? { create: newName.trim() }
        : (items || []).find((i) => i.value === chosen)!;
      await state.onPick(target);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const canConfirm = chosen === '__new__' ? newName.trim().length > 0 : !!chosen;

  return (
    <ModalShell
      isOpen
      onClose={onClose}
      title={state.title}
      icon={state.icon}
      size="md"
      zIndexClassName="z-[3200]"
      data-testid="files-target-picker"
      footer={(
        <>
          <Button variant="outline" size="small" onClick={onClose}>{t('Cancel')}</Button>
          <Button variant="primary" size="small" onClick={confirm} disabled={!canConfirm} isLoading={busy}>
            {state.confirmLabel}
          </Button>
        </>
      )}
    >
      <div className="space-y-3">
        <label className="flex items-center gap-2 rounded-md border border-hairline bg-field px-2.5 py-1.5">
          <PiMagnifyingGlassLight className="h-4 w-4 text-fg-muted" aria-hidden />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('Search')}
            aria-label={t('Search')}
            className="w-full bg-transparent text-sm focus:outline-none"
          />
        </label>
        <div className="max-h-72 space-y-1 overflow-y-auto" role="listbox" aria-label={state.title}>
          {state.createLabel && (
            <div className={`rounded-md border p-2 ${chosen === '__new__' ? 'border-champagne/60 bg-champagne/5' : 'border-hairline-soft'}`}>
              <button
                type="button"
                role="option"
                aria-selected={chosen === '__new__'}
                onClick={() => setChosen('__new__')}
                className="flex w-full items-center gap-2 text-start text-sm text-fg-strong"
              >
                <PiPlusLight className="h-4 w-4" aria-hidden /> {state.createLabel}
              </button>
              {chosen === '__new__' && (
                <input
                  autoFocus
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder={t('Name')}
                  aria-label={t('Name')}
                  className="mt-2 w-full rounded-md border border-hairline bg-field px-2 py-1 text-sm"
                />
              )}
            </div>
          )}
          {items === null ? (
            <Skeleton variant="list" rows={4} />
          ) : items.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted">{t('Nothing found')}</p>
          ) : items.map((i) => (
            <button
              key={i.value}
              type="button"
              role="option"
              aria-selected={chosen === i.value}
              onClick={() => setChosen(i.value)}
              className={`block w-full rounded-md border px-3 py-2 text-start text-sm transition-colors ${
                chosen === i.value ? 'border-champagne/60 bg-champagne/5 text-fg-strong' : 'border-transparent hover:bg-surface-sunken/70 text-fg-body'
              }`}
            >
              <span className="block truncate">{i.label}</span>
              {i.hint && <span className="block truncate text-xs text-fg-muted">{i.hint}</span>}
            </button>
          ))}
        </div>
      </div>
    </ModalShell>
  );
};

export function useFilesIn({ t, dispatch, history }: { t: TFunction; dispatch: AppDispatch; history: { push: (p: string) => void } }) {
  const [picker, setPicker] = useState<PickerState | null>(null);

  const toast = useCallback((modalType: 'success' | 'error', message: string) => {
    dispatch(showFeedbackModal({ showModal: true, modalType, message, duration: 5000 }) as any);
  }, [dispatch]);

  const startReview = useCallback((sel: FilesSelection) => {
    dispatch(showModal({
      type: 'playbookPicker',
      showModal: true,
      onPlaybookSelect: async (playbook: any) => {
        if (!playbook?.uuid) return;
        try {
          const res = await apis.post('/reviews/from-files', {
            file_uuids: sel.uuids,
            playbook_uuid: playbook.uuid,
            name: sel.uuids.length === 1 ? undefined : `${t('Review')} — ${sel.names[0]} +${sel.uuids.length - 1}`,
          });
          const uuid = res.data?.session_uuid || res.data?.uuid || res.data?.data?.session_uuid;
          if (uuid) history.push(`/reviews/${uuid}`);
        } catch (e) {
          toast('error', errorText(e, t('Could not start the review.')));
        }
      },
    } as any) as any);
  }, [dispatch, history, t, toast]);

  const addToTable = useCallback((sel: FilesSelection) => setPicker({
    title: t('Add to a table'),
    icon: <PiTableLight />,
    confirmLabel: t('Add files'),
    createLabel: t('New table'),
    load: async (search) => {
      const res = await apis.get('/tables', { params: { page: 1, page_size: 50, ...(search ? { search_term: search } : {}) } });
      return (res.data?.items || []).map((x: any) => ({ value: x.uuid, label: x.name || t('Untitled'), hint: x.document_count != null ? `${x.document_count} ${t('documents')}` : undefined }));
    },
    onPick: async (target) => {
      try {
        let uuid = 'value' in target ? target.value : '';
        if ('create' in target) {
          const created = await apis.post('/tables', { name: target.create });
          uuid = created.data?.uuid || created.data?.table?.uuid || created.data?.data?.uuid;
        }
        await apis.post(`/tables/${uuid}/documents/from-files`, { file_uuids: sel.uuids });
        history.push(`/tables/${uuid}`);
      } catch (e) {
        toast('error', errorText(e, t('Could not add the files to the table.')));
        throw e;
      }
    },
  }), [history, t, toast]);

  const askInResearch = useCallback(async (sel: FilesSelection) => {
    try {
      const created = await apis.post('/research/chat-groups', {});
      const uuid = created.data?.uuid || created.data?.data?.uuid;
      await apis.post(`/research/chat-groups/${uuid}/attachments/from-files`, { file_uuids: sel.uuids });
      history.push(`/research/${uuid}`);
    } catch (e) {
      toast('error', errorText(e, t('Could not start research with these files.')));
    }
  }, [history, t, toast]);

  const addToMatter = useCallback((sel: FilesSelection) => setPicker({
    title: t('Add to a matter'),
    icon: <PiBriefcaseLight />,
    confirmLabel: t('Add files'),
    load: async (search) => {
      const res = await apis.get('/matters/view', { params: { page: 1, page_size: 50, searchTerm: search, filters: '{}' } });
      const rows = res.data?.matters || res.data?.data || [];
      return rows.map((m: any) => ({ value: m.uuid || m.matter_uuid, label: m.title || m.Title || m.MatterName || t('Untitled') }))
        .filter((m: Target) => m.value);
    },
    onPick: async (target) => {
      if (!('value' in target)) return;
      try {
        await apis.post(`/matters/${target.value}/documents/from-files`, { file_uuids: sel.uuids });
        toast('success', t('Added to {{name}}', { name: target.label }));
      } catch (e) {
        toast('error', errorText(e, t('Could not add the files to the matter.')));
        throw e;
      }
    },
  }), [t, toast]);

  const linkToProject = useCallback((sel: FilesSelection) => setPicker({
    title: t('Link to a project'),
    icon: <PiKanbanLight />,
    confirmLabel: t('Link files'),
    load: async (search) => {
      const res = await apis.get('/projects/linkable');
      const q = search.trim().toLowerCase();
      return (res.data?.projects || [])
        .map((p: any) => ({ value: p.uuid, label: p.title || p.name || t('Untitled') }))
        .filter((p: Target) => !q || p.label.toLowerCase().includes(q));
    },
    onPick: async (target) => {
      if (!('value' in target)) return;
      try {
        for (const uuid of sel.uuids) {
          // eslint-disable-next-line no-await-in-loop
          await apis.post(`/projects/${target.value}/resources`, { resource_type: 'file', resource_uuid: uuid });
        }
        toast('success', t('Linked to {{name}}', { name: target.label }));
      } catch (e) {
        toast('error', errorText(e, t('Could not link the files to the project.')));
        throw e;
      }
    },
  }), [t, toast]);

  const runScan = useCallback((sel: FilesSelection) => {
    dispatch(showSidePanel({
      type: 'scan', showSidePanel: true, size: 'md', message: t('Legislation scan'), subMsg: sel.names[0],
      target: { kind: 'file', fileUuid: sel.uuids[0] },
    } as any) as any);
  }, [dispatch, t]);

  /** The "Use in…" entries for a selection of FILES (no folders). */
  const actionsFor = useCallback((sel: FilesSelection) => {
    if (sel.uuids.length === 0) return [];
    return [
      { key: 'review', label: t('Start a review'), icon: <PiFileMagnifyingGlassLight className="h-4 w-4" />, onClick: () => startReview(sel) },
      { key: 'table', label: t('Add to a table'), icon: <PiTableLight className="h-4 w-4" />, onClick: () => addToTable(sel) },
      { key: 'research', label: t('Ask in Research'), icon: <PiChatCircleDotsLight className="h-4 w-4" />, onClick: () => askInResearch(sel) },
      { key: 'matter', label: t('Add to a matter'), icon: <PiBriefcaseLight className="h-4 w-4" />, onClick: () => addToMatter(sel) },
      { key: 'project', label: t('Link to a project'), icon: <PiKanbanLight className="h-4 w-4" />, onClick: () => linkToProject(sel) },
      ...(sel.uuids.length === 1
        ? [{ key: 'scan', label: t('Run legislation scan'), icon: <PiScalesLight className="h-4 w-4" />, onClick: () => runScan(sel) }]
        : []),
    ];
  }, [t, startReview, addToTable, askInResearch, addToMatter, linkToProject, runScan]);

  const modal = useMemo(
    () => (picker ? <TargetPicker state={picker} t={t} onClose={() => setPicker(null)} /> : null),
    [picker, t],
  );

  return { actionsFor, modal };
}
